import { DeviceSubCategory, type DeviceSubCategoryConfig } from '@eppendorf/app-configs';
import {
  ButtonBase,
  Checkbox,
  DropdownMenu,
  Icon,
  IconSizeClasses,
  Tooltip,
} from '@eppendorf/vnls-react-components';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { DeviceCategoryConfig } from '$components/app-config/app-config.types';

import {
  CategoryCardHeader,
  NotificationCategoryCardProps,
} from './notification-category-card';

const rangeSubCategoryKeys: DeviceSubCategory[][] = [
  [DeviceSubCategory.CRITICAL_LOW, DeviceSubCategory.ALERT_LOW],
  [DeviceSubCategory.WARN_LOW],
  [DeviceSubCategory.VALUE_GOOD],
  [DeviceSubCategory.WARN_HIGH],
  [DeviceSubCategory.CRITICAL_HIGH, DeviceSubCategory.ALERT_HIGH],
];

const rangeVocabulary = new Set(rangeSubCategoryKeys.flat());

export const isRangeCategory = (category: DeviceCategoryConfig) =>
  category.subCategories.length > 0 &&
  category.subCategories.every((subCategory) => rangeVocabulary.has(subCategory.key));

export type MonitoringDeviceLink = {
  manufacturer: string;
  model?: string;
  name: string;
  serialNumber: string;
};

function createMonitoringPath({
  manufacturer,
  serialNumber,
}: MonitoringDeviceLink): string {
  const searchParams = new URLSearchParams({
    sidecarRoute: 'monitoring/parameters',
    manufacturer,
    serialNumber,
  });

  return `/monitoring?${searchParams.toString()}`;
}

function MonitoringDevicesMenu({ devices }: { devices: MonitoringDeviceLink[] }) {
  const { t } = useTranslation();
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);

  if (!devices.length) return null;

  if (devices.length === 1) {
    return (
      <a
        className="condition-range-card__monitoring-trigger"
        href={createMonitoringPath(devices[0])}
        target="_blank"
        rel="noopener noreferrer"
      >
        {t('tagsCondition.thresholdsInMonitoring')}
        <Icon name="external-link" size={IconSizeClasses.XSmall} />
      </a>
    );
  }

  return (
    <div className="condition-range-card__monitoring-menu" ref={setPortalContainer}>
      <DropdownMenu
        trigger={
          <ButtonBase className="condition-range-card__monitoring-trigger color-blue-600">
            {t('tagsCondition.thresholdsInMonitoring')}
            <Icon name="down" size={IconSizeClasses.XSmall} />
          </ButtonBase>
        }
        portalContainer={portalContainer}
        className="condition-range-card__device-list"
        side="bottom"
        align="end"
      >
        {devices.map((device) => (
          <DropdownMenu.Item
            key={`${device.manufacturer}-${device.serialNumber}`}
            className="condition-range-card__device-link"
            asChild
          >
            <a
              href={createMonitoringPath(device)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="condition-range-card__device-name">{device.name}</span>
              <Icon name="external-link" size={IconSizeClasses.XSmall} />
            </a>
          </DropdownMenu.Item>
        ))}
      </DropdownMenu>
    </div>
  );
}

function SnowflakeIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <path
        d="M19.9839 6.41699L17.0279 9.37305V14.2783L21.2076 11.8662L22.3072 7.76074L24.2447 8.2793L23.3052 11.7832L26.8101 12.7227L26.2916 14.6592L22.2535 13.5771L18.0572 15.999L22.2515 18.4209L26.2916 17.3379L26.8101 19.2754L23.3033 20.2139L24.2437 23.7197L22.3082 24.2383L21.2066 20.1309L17.0279 17.7188V22.6279L19.9839 25.584L18.567 27.001L15.9976 24.4326L13.4332 26.9971L12.0162 25.5801L15.023 22.5723V17.751L10.7759 20.2031L9.69391 24.2412L7.75739 23.7217L8.69684 20.2168L5.19098 19.2783L5.70953 17.3418L9.81598 18.4424L14.0474 15.998L9.81696 13.5566L5.70953 14.6572L5.19 12.7207L8.69586 11.7822L7.75739 8.27539L9.69293 7.75684L10.7759 11.7959L15.023 14.2471V9.42773L12.0162 6.4209L13.4332 5.00391L15.9976 7.56738L18.567 5L19.9839 6.41699Z"
        fill="currentColor"
      />
    </svg>
  );
}

export function NotificationRangeCategoryCard({
  category,
  selectedSubCategories,
  onToggle,
  monitoringDevices,
}: NotificationCategoryCardProps & { monitoringDevices: MonitoringDeviceLink[] }) {
  const { t } = useTranslation();
  const cardId = useId();
  const rangeSubCategories = rangeSubCategoryKeys.map((keys) =>
    category.subCategories.filter((subCategory) => keys.includes(subCategory.key)),
  );
  const isTemperatureCategory = category.key.toLowerCase().includes('temperature');

  const renderSlot = (subCategories: DeviceSubCategoryConfig[], slotIndex: number) => {
    if (!subCategories.length) {
      return (
        <div
          key={`empty-${slotIndex}`}
          className="condition-range-card__slot"
          aria-hidden="true"
        />
      );
    }

    return (
      <div key={subCategories[0].key} className="condition-range-card__slot">
        {subCategories.map((subCategory) => {
          const id = `${cardId}-${subCategory.key}`;
          const label = subCategory.name;

          return (
            <div key={subCategory.key} className="condition-range-card__option">
              <Checkbox
                id={id}
                checked={selectedSubCategories.includes(subCategory.key)}
                onChange={() => onToggle(subCategory.key)}
                ariaLabel={`${category.name}: ${label}`}
              />
              <label htmlFor={id} className="condition-range-card__label">
                <span>{label}</span>
              </label>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <section className="condition-category-card condition-range-card">
      <div className="condition-range-card__heading-row">
        <CategoryCardHeader category={category} />
        <MonitoringDevicesMenu devices={monitoringDevices} />
      </div>
      <div className="condition-range-card__scroll">
        <div className="condition-range-card__content">
          <div className="condition-range-card__slots">
            {rangeSubCategories.map(renderSlot)}
          </div>
          <div className="condition-range-card__bar-row">
            <Tooltip
              trigger={
                <ButtonBase
                  className="condition-range-card__endpoint condition-range-card__endpoint--low"
                  aria-label={t('tagsCondition.advanced.lowerLimit')}
                >
                  {isTemperatureCategory ? (
                    <SnowflakeIcon />
                  ) : (
                    <Icon name="pointer-down" size={IconSizeClasses.XSmall} />
                  )}
                </ButtonBase>
              }
            >
              {t('tagsCondition.advanced.lowerLimit')}
            </Tooltip>
            <div className="condition-range-card__bands" aria-hidden="true">
              <span className="condition-range-card__band condition-range-card__band--critical" />
              <span className="condition-range-card__band condition-range-card__band--warning" />
              <span className="condition-range-card__band condition-range-card__band--normal" />
              <span className="condition-range-card__band condition-range-card__band--warning" />
              <span className="condition-range-card__band condition-range-card__band--critical" />
            </div>
            <Tooltip
              trigger={
                <ButtonBase
                  className="condition-range-card__endpoint condition-range-card__endpoint--high"
                  aria-label={t('tagsCondition.advanced.upperLimit')}
                >
                  <Icon
                    name={isTemperatureCategory ? 'brightness' : 'pointer-top'}
                    size={IconSizeClasses.XSmall}
                  />
                </ButtonBase>
              }
            >
              {t('tagsCondition.advanced.upperLimit')}
            </Tooltip>
          </div>
        </div>
      </div>
    </section>
  );
}
